import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { addDays, format, isSameDay } from 'date-fns'
import { uk, enUS, ru } from 'date-fns/locale'
import { Sparkles } from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { useT } from '@/lib/i18n'
import { localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { Markdown } from '@/components/Markdown'
import {
  StorefrontCard,
  StorefrontEmptyState,
  StorefrontLoading,
  StorefrontPage,
} from '@/components/StorefrontPage'
import type {
  ServiceDetail,
  ServiceSummary,
  ServiceProvider,
  AvailabilityOut,
  CartServiceAddResult,
} from '@/shared/types'

function ServiceList({
  onSelect,
}: {
  onSelect: (id: number) => void
}) {
  const lang = useAppStore((s) => s.lang)
  const T = useT('services')
  const common = useT('common')

  const { data: services, isLoading } = useQuery<ServiceSummary[]>({
    queryKey: ['services'],
    queryFn: () => api.get<ServiceSummary[]>('/services'),
  })

  if (isLoading) return <StorefrontLoading message={common.loading} />
  if (!services?.length) {
    return (
      <StorefrontEmptyState
        icon={Sparkles}
        title={T.empty}
      />
    )
  }

  return (
    <div className="space-y-3">
      {services.map((s) => {
        const name = localizedName(lang, s.name_uk, s.name_en)
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s.id)}
            className="w-full text-left focus:outline-none"
          >
            <StorefrontCard
              interactive
              className="overflow-hidden p-3.5 flex items-center gap-3.5"
            >
              {s.photo_url ? (
                <img
                  src={s.photo_url}
                  alt={name}
                  className="h-16 w-16 rounded-xl object-cover shrink-0 border border-slate-100"
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                  <Sparkles className="h-6 w-6 opacity-60" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 truncate">{name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {s.price_uah} UAH · {s.duration_min} {T.min}
                </p>
              </div>
            </StorefrontCard>
          </button>
        )
      })}
    </div>
  )
}

function ServiceBooking({ serviceId, onBack }: { serviceId: number; onBack: () => void }) {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const T = useT('services')
  const common = useT('common')
  const qc = useQueryClient()

  const [providerId, setProviderId] = useState<number | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [info, setInfo] = useState<string | null>(null)
  const navigate = useNavigate()

  const { data: service, isLoading } = useQuery<ServiceDetail>({
    queryKey: ['service', serviceId],
    queryFn: () => api.get<ServiceDetail>(`/services/${serviceId}`),
  })

  useEffect(() => {
    if (service?.providers.length === 1) setProviderId(service.providers[0].id) // eslint-disable-line react-hooks/set-state-in-effect
  }, [service])

  const activeProviderId = providerId ?? service?.providers[0]?.id ?? null

  const { data: availability } = useQuery<AvailabilityOut>({
    queryKey: ['availability', serviceId, activeProviderId, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: () =>
      api.get<AvailabilityOut>(
        `/services/${serviceId}/availability?provider_id=${activeProviderId}&date=${format(selectedDate, 'yyyy-MM-dd')}`,
      ),
    enabled: Boolean(activeProviderId),
  })

  const addMutation = useMutation<CartServiceAddResult, ApiError>({
    mutationFn: () =>
      api.post<CartServiceAddResult>('/cart/add-service', {
        service_id: serviceId,
        provider_id: activeProviderId,
        starts_at: selectedSlot,
        customer_notes: notes,
      }),
    onSuccess: async (result) => {
      haptic('success')
      const cart = await api.get<{ count: number }>('/cart')
      setCartCount(cart.count)
      qc.invalidateQueries({ queryKey: ['cart'] })
      if (result.calendar_conflict) {
        setInfo(T.calendarConflict)
        setTimeout(() => void navigate({ to: '/cart' }), 2500)
      } else {
        void navigate({ to: '/cart' })
      }
    },
    onError: (err) => {
      haptic('error')
      if (err.message === 'slot_taken') {
        qc.invalidateQueries({ queryKey: ['availability'] })
      }
    },
  })

  if (isLoading || !service) {
    return (
      <StorefrontPage onBack={onBack} backLabel={T.back}>
        <StorefrontLoading message={common.loading} />
      </StorefrontPage>
    )
  }

  const name = localizedName(lang, service.name_uk, service.name_en)
  const desc = localizedName(lang, service.description_uk, service.description_en)
  const days = Array.from({ length: 10 }, (_, i) => addDays(new Date(), i))
  const locale = lang === 'en' ? enUS : lang === 'ru' ? ru : uk

  return (
    <StorefrontPage onBack={onBack} backLabel={T.back} className="pb-32">
      <StorefrontCard className="overflow-hidden mb-4">
        {service.photo_url && (
          <img src={service.photo_url} alt={name} className="h-48 w-full object-cover" />
        )}
        <div className="p-4">
          <h1 className="text-xl font-bold text-slate-900">{name}</h1>
          <p className="mt-1.5 text-sm font-semibold text-brand-600">
            {service.price_uah} UAH · <span className="text-slate-500 font-normal">{service.duration_min} {T.min}</span>
          </p>
          {desc && <Markdown content={desc} className="mt-3 text-xs text-slate-600 space-y-2 border-t border-slate-100 pt-3" />}
        </div>
      </StorefrontCard>

      {/* Provider picker */}
      {service.providers.length > 1 && (
        <StorefrontCard className="p-4 mb-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{T.provider}</p>
          <div className="flex flex-wrap gap-2">
            {service.providers.map((p: ServiceProvider) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setProviderId(p.id); setSelectedSlot(null) }}
                className={`rounded-xl px-3.5 py-1.5 text-xs font-semibold border transition ${
                  activeProviderId === p.id
                    ? 'bg-brand-600 text-white border-brand-600 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </StorefrontCard>
      )}

      {/* Date picker */}
      <StorefrontCard className="p-4 mb-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{T.date}</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {days.map((d) => (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => { setSelectedDate(d); setSelectedSlot(null) }}
              className={`flex-shrink-0 rounded-xl px-3.5 py-2 text-center text-xs border transition ${
                isSameDay(d, selectedDate)
                  ? 'bg-brand-600 text-white border-brand-600 shadow-xs font-bold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="block text-[10px] opacity-80 uppercase">{format(d, 'EEE', { locale })}</span>
              <span className="text-sm font-semibold">{format(d, 'dd.MM')}</span>
            </button>
          ))}
        </div>
      </StorefrontCard>

      {/* Slot picker */}
      <StorefrontCard className="p-4 mb-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{T.time}</p>
        {!availability?.slots.length ? (
          <p className="text-xs text-slate-400 py-2">{T.noSlots}</p>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {availability.slots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setSelectedSlot(slot)}
                className={`rounded-xl border py-2.5 text-xs font-semibold transition ${
                  selectedSlot === slot
                    ? 'bg-brand-600 text-white border-brand-600 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                {slot.slice(11, 16)}
              </button>
            ))}
          </div>
        )}
      </StorefrontCard>

      {/* Notes */}
      <StorefrontCard className="p-4 mb-4">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{T.notes}</label>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ваші побажання або коментар..."
          className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white transition"
        />
      </StorefrontCard>

      {/* Feedback messages */}
      {addMutation.error && (
        <p className="mb-4 px-1 text-xs font-semibold text-rose-600">
          {addMutation.error.message === 'slot_taken' ? T.slotTaken : addMutation.error.message}
        </p>
      )}
      {info && <p className="mb-4 px-1 text-xs font-semibold text-amber-600">{info}</p>}

      {/* Add button */}
      <div className="app-bottom-fixed app-bottom-panel border-t bg-white/95 backdrop-blur-md p-3.5 shadow-lg">
        <button
          disabled={!selectedSlot || addMutation.isPending}
          onClick={() => { haptic('medium'); addMutation.mutate() }}
          className="w-full rounded-xl bg-brand-600 hover:bg-brand-700 active:scale-[0.99] py-3 text-xs font-bold text-white shadow-xs transition disabled:opacity-40"
        >
          {addMutation.isPending ? T.adding : T.add}
        </button>
      </div>
    </StorefrontPage>
  )
}

export function ServicesPage() {
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null)
  const T = useT('services')

  if (selectedServiceId !== null) {
    return (
      <ServiceBooking
        serviceId={selectedServiceId}
        onBack={() => setSelectedServiceId(null)}
      />
    )
  }

  return (
    <StorefrontPage
      title={T.title}
      subtitle="Оберіть послугу для онлайн-запису"
    >
      <ServiceList onSelect={setSelectedServiceId} />
    </StorefrontPage>
  )
}
